/**
 * Lightweight wrapper around GA4 gtag.
 *
 * The gtag script + initial config live in `public/index.html` so the
 * page_view fires before React mounts. This module just gives the
 * React codebase a one-liner to emit custom events and identify
 * users — without sprinkling `window.gtag(...)` calls everywhere.
 *
 * All functions are no-ops when gtag isn't on the page (e.g. an
 * ad-blocker stripped it, or a unit test runs without index.html).
 * That means consumers never need a try/catch around tracking calls.
 */

/** Fire a custom event. Params are flattened directly into the GA event. */
export function track(eventName, params) {
    try {
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
        window.gtag('event', eventName, params || {});
    } catch (_e) { /* analytics must never break the app */ }
}

// Set of events that we ALSO mirror through our own backend (which
// then mirrors to GA4 Measurement Protocol server-side). Ad-blockers
// can strip the gtag call above but can't intercept a same-origin
// fetch to our own API, so these funnel-critical events land in the
// marketing dashboard even when the browser is heavily locked-down.
// Keep the list tight — every event here costs a network round-trip.
const SERVER_MIRRORED_EVENTS = new Set([
    'sign_up',
    'first_save',
    'first_ai_question',
    'upgrade_purchased',
]);

/** GA4 client_id lookup — reads `_ga` cookie shape `GA1.1.<client_id>.<ts>`. */
function _readGa4ClientId() {
    try {
        if (typeof document === 'undefined') return null;
        const m = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([^;]+)/);
        return m ? m[1] : null;
    } catch (_) { return null; }
}

/**
 * Fire an event through BOTH the browser gtag and our own backend
 * sink. Use this for anything the marketing dashboard needs to trust
 * — sign_up, first_save, first_ai_question, upgrade_purchased.
 *
 * The server enforces once-per-user semantics for the activation
 * events, so it's safe to call this on every save/ask — the second
 * call for the same user is a no-op both in Mongo AND in GA4 (MP
 * dedupes on user_id+event within the same second).
 */
export function trackServer(eventName, params) {
    // Client-side gtag still fires so users WITHOUT ad-blockers get
    // real-time GA4 signals (Realtime tab, DebugView, etc.).
    track(eventName, params);

    if (!SERVER_MIRRORED_EVENTS.has(eventName)) return;
    try {
        const clientId = _readGa4ClientId();
        // fetch() to our own origin — same-site, cookie-authed. `keepalive`
        // so a fast navigation right after the event (e.g. modal close +
        // route change) doesn't abort the request in-flight.
        fetch('/api/analytics/event', {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                ...(clientId ? { 'X-BrainSync-CID': clientId } : {}),
            },
            body: JSON.stringify({
                event_name: eventName,
                params: params || {},
                client_id: clientId || undefined,
            }),
        }).catch(() => { /* backend outage should never break UX */ });
    } catch (_e) { /* swallow */ }
}

/**
 * Associate the current session with a backend user id. Call once on
 * sign-in success. We deliberately do NOT pass email / display name —
 * GA4's user_id is a hashed opaque identifier and storing PII as a
 * user-property is a quick way to land in a GDPR violation.
 */
export function identify(userId, traits) {
    try {
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
        if (userId) {
            window.gtag('set', { user_id: String(userId) });
        }
        if (traits && typeof traits === 'object') {
            window.gtag('set', 'user_properties', traits);
        }
    } catch (_e) { /* swallow */ }
}

/** Clear the user_id on sign-out so the same browser doesn't bleed into the next account. */
export function resetIdentity() {
    try {
        if (typeof window === 'undefined') return;
        if (typeof window.gtag === 'function') {
            window.gtag('set', { user_id: null });
        }
        // Microsoft Clarity — end the current session so the next
        // recording starts fresh under an anonymous user. Prevents
        // logout-then-login-as-different-user sessions from being
        // stitched under the previous user_id.
        if (typeof window.clarity === 'function') {
            window.clarity('set', 'plan_tier', 'anonymous');
            window.clarity('set', 'user_id', 'anonymous');
        }
    } catch (_e) { /* swallow */ }
}

/** Manual page_view fire — wire this to react-router so SPA route
 * changes are tracked. `send_page_view: true` in index.html only
 * captures the initial load.
 */
export function trackPageView(path) {
    try {
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
        window.gtag('event', 'page_view', {
            page_path: path || (window.location.pathname + window.location.search),
            page_location: window.location.href,
            page_title: document.title
        });
    } catch (_e) { /* swallow */ }
}

// ── UTM capture ──────────────────────────────────────────────────
// Google Ads App Campaigns doesn't need this for install attribution
// (Play Console + Ads auto-link), but any web-side landing hits
// (`?utm_source=google&utm_medium=cpc&utm_campaign=android-launch`)
// should stash the params so we can attach them to sign_up / first_save
// events — this is what lets us attribute web conversions back to the
// campaign that drove them.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'];
const UTM_STORAGE_KEY = 'bs_utm';
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Reads UTM params from the current URL and stashes them (with a
 * timestamp) in localStorage so they survive OAuth round-trips and
 * subsequent visits within the attribution window. Safe to call on
 * every landing page mount — no-ops when no UTM params are present.
 */
export function captureUtmParams() {
    try {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const found = {};
        UTM_KEYS.forEach(k => {
            const v = params.get(k);
            if (v) found[k] = v;
        });
        if (Object.keys(found).length === 0) return;
        found.captured_at = Date.now();
        localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
    } catch (_e) { /* swallow */ }
}

/** Returns the currently-attributed UTM params object (or {} if none / expired). */
export function getUtmParams() {
    try {
        if (typeof window === 'undefined') return {};
        const raw = localStorage.getItem(UTM_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || (parsed.captured_at && Date.now() - parsed.captured_at > UTM_TTL_MS)) {
            localStorage.removeItem(UTM_STORAGE_KEY);
            return {};
        }
        // Strip our internal timestamp before returning to callers.
        const { captured_at: _ts, ...rest } = parsed;
        return rest;
    } catch (_e) {
        return {};
    }
}
